import React from 'react';
import { ROUTE_INFOS, RouteInfo } from '../../../../routes';
import MenuItem from './MenuItem';
import { useNavigate } from 'react-router-dom';
import { useZusListStore } from '../../../../zustand/store';

import { useRoutePermission, useTranslation } from '../../../../hooks';
import MenuAccordion from './MenuAccordion';
import { findItemWithParents } from '../helper/findItemWithParents';
import { getCurrentActiveMenuKey } from '../helper/currentActiveMenu';
import { useAppSelector } from '../../../../reducer/hooks';
import { selectIsMobileView } from '../../../../reducer/mediaSlice';
import Home from './Home';
import { selectProfile } from '../../../../reducer/profileSlice';

type MenuItem = RouteInfo & { acceptLevel?: number };
interface IProps {
  closeMobileSideMenu?: () => void;
}

const RecursiveMenu = (props: IProps) => {
  const { closeMobileSideMenu } = props;

  const [currentActiveKeys, setCurrentActiveKeys] = React.useState<string[]>(
    findItemWithParents(getCurrentActiveMenuKey()?.[0])
  );

  const navigate = useNavigate();
  const zusListStore = useZusListStore();
  const { translate } = useTranslation();
  const { hasRoutePermission } = useRoutePermission();
  const isMobile = useAppSelector(selectIsMobileView);
  const profile = useAppSelector(selectProfile);

  const handleMenuItemClick = (menuItem: MenuItem) => {
    const { key, route, acceptLevel } = menuItem;
    const localRoute =
      route === 'transactionRequest' ? `${route}?acceptLevel=${acceptLevel}` : route;

    setCurrentActiveKeys(findItemWithParents(key));
    navigate(localRoute || '');
    zusListStore.clear();
    closeMobileSideMenu && closeMobileSideMenu();
  };

  const hasPermissionForAnyChild = (routeInfo: RouteInfo): boolean => {
    if (!routeInfo.children || routeInfo.children.length === 0) {
      return hasRoutePermission(routeInfo.key);
    }

    // hide accordion if don't have any child accessible
    return routeInfo.children.some((child) => hasPermissionForAnyChild(child));
  };

  const createMenus = (routeInfos: RouteInfo[]) => {
    return routeInfos.map((routeInfo) => {
      // For Menu Group
      if (routeInfo.children && routeInfo.children.length > 0) {
        const noItemsHavePermission = !hasPermissionForAnyChild(routeInfo);
        if (noItemsHavePermission || routeInfo.isHideMenu) {
          return null;
        }

        const parents = findItemWithParents(routeInfo.key);

        return (
          <MenuAccordion
            key={routeInfo.key}
            title={translate(`menu.${routeInfo.key}`)}
            routeInfo={routeInfo}
            currentActiveKeys={currentActiveKeys}
            setCurrentActiveKeys={setCurrentActiveKeys}
            layers={parents.length}
          >
            <>{routeInfo.children && createMenus(routeInfo.children)}</>
          </MenuAccordion>
        );
      }

      // For Menu Item
      if (routeInfo.isHideMenu) return null;
      if (!hasRoutePermission(routeInfo.key)) return null;
      if (isMobile && routeInfo.isHiddenInMobileView) return null;

      if (routeInfo.route === 'transactionRequest') {
        return profile.acceptLevel.map((item) => {
          const key = `${routeInfo.key}-${item}`;

          const hasLevelPermission = profile.featureList.some(
            (featureCode) => featureCode === `transaction_request/view/level_${item}`
          );
          if (!hasLevelPermission) {
            return <div key={key} />;
          }

          return (
            <MenuItem
              key={key}
              title={`${translate(`menu.${routeInfo.key}`)} - ${item}`}
              routeInfo={routeInfo}
              handleMenuItemClick={() => {
                handleMenuItemClick({ ...routeInfo, key: key, acceptLevel: item });
              }}
              acceptLevel={item}
            />
          );
        });
      }

      return (
        <MenuItem
          key={routeInfo.key}
          title={translate(`menu.${routeInfo.key}`)}
          routeInfo={routeInfo}
          handleMenuItemClick={() => handleMenuItemClick(routeInfo)}
        />
      );
    });
  };

  const handleOnHomeClicked = () => {
    setCurrentActiveKeys([]);
    zusListStore.clear();
    closeMobileSideMenu && closeMobileSideMenu();
  };

  return (
    <>
      <Home onHomeClicked={handleOnHomeClicked} />
      {createMenus(ROUTE_INFOS)}
    </>
  );
};

export default RecursiveMenu;
